import { useCallback, useEffect, useState } from 'react';
import type { IPublicWorkArea } from '@featit/shared';
import {
  PublicReadError,
  publicReadApi,
  type PublicReadFailureType,
} from '@/services/PublicReadApi';

export type PublicLookupType =
  | { kind: 'loading' }
  | { kind: 'found'; workArea: IPublicWorkArea }
  | { kind: 'not-found' }
  | { kind: 'limited'; retryAfterSeconds: number }
  | { kind: 'failed' };

/**
 * Asks the signed-out surface whether `reference` is a Public work area (F-0338 AC-PV21). One request;
 * `not-found` is the answer for every kind of "no" (AC-PV6), so a caller falls back to what it did
 * before — a sign-in for a visitor, "no such work area" for a member.
 */
export function usePublicWorkArea(reference: string | null): {
  lookup: PublicLookupType;
  retry: () => void;
} {
  const [lookup, setLookup] = useState<PublicLookupType>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (reference === null) {
      setLookup({ kind: 'not-found' });
      return;
    }
    let current = true;
    setLookup({ kind: 'loading' });
    publicReadApi
      .workArea(reference)
      .then((workArea) => current && setLookup({ kind: 'found', workArea }))
      .catch((error: unknown) => {
        if (!current) return;
        const failure: PublicReadFailureType =
          error instanceof PublicReadError ? error.failure : { kind: 'failed' };
        setLookup(failure.kind === 'not-found' ? { kind: 'not-found' } : failure);
      });
    return () => {
      current = false;
    };
  }, [reference, attempt]);

  return { lookup, retry: useCallback(() => setAttempt((value) => value + 1), []) };
}
